"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, Flame, Scale, ShieldAlert } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const applicationsData = [{
  id: "cbrne",
  badge: "Defense & Civil Protection",
  title: "CBRNe & HazMat Response",
  iconName: "shield",
  description: "Rapid, non-contact identification of unknown solid and liquid chemical agents, toxic industrial chemicals, and precursor materials in high-risk zones.",
  pointsText1: "Through-barrier analysis preserving sample integrity",
  pointsText2: "Ruggedized for deployment in hot-zone environments",
  pointsText3: "Integrated defense spectral libraries",
  linkPath: "/SafetyAndSecurityPage",
  ctaText: "Explore Defense Solutions"
}, {
  id: "fire-hazmat",
  badge: "Emergency Services",
  title: "Fire Services & HazMat Response",
  iconName: "flame",
  description: "On-scene screening of unidentified chemical spills, volatile substances, and industrial accelerants to guide immediate containment and PPE safety protocols.",
  pointsText1: "Immediate hazard classification without sample prep",
  pointsText2: "Non-destructive testing inside translucent containers",
  pointsText3: "Fast decision support for first-in responders",
  linkPath: "/SafetyAndSecurityPage",
  ctaText: "View HazMat Tools"
}, {
  id: "law-enforcement",
  badge: "Customs & Policing",
  title: "Law Enforcement & Interdiction",
  iconName: "scale",
  description: "Point-of-seizure confirmation of illicit narcotics, cutting agents, novel psychoactive substances (NPS), and counterfeit pharmaceuticals at borders and checkpoints.",
  pointsText1: "Direct identification of trace and bulk narcotics",
  pointsText2: "Chain-of-custody and evidence verification support",
  pointsText3: "Continuous library updates for synthetic analogues",
  linkPath: "/LawEnforcementPublicSafetyPage",
  ctaText: "Explore Interdiction Tools"
}];
export default function OperationalApplications() {
  const prefersReducedMotion = useReducedMotion();
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case "shield":
        return <ShieldAlert className="size-6 text-primary" />;
      case "flame":
        return <Flame className="size-6 text-primary" />;
      case "scale":
        return <Scale className="size-6 text-primary" />;
      default:
        return <ShieldAlert className="size-6 text-primary" />;
    }
  };
  return <section id="operationalapplications" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="h-2 w-2 rounded-full bg-primary" />
            <span className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">
              Field Deployments
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-foreground mb-4">
            Operational Applications
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Analytical tools engineered for high-stakes field environments, delivering reliable, immediate material identification where lab access is unavailable.
          </p>
        </div>

        {/* 3-Card Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {applicationsData.map((app, i) => <motion.div key={app.id} data-index={i} initial={prefersReducedMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={prefersReducedMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i * 0.1
        }} className="flex">
              <Card className="flex flex-col justify-between w-full border-border bg-card shadow-sm hover:shadow-md transition-shadow">
                <div>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="p-2.5 rounded-lg bg-secondary flex items-center justify-center">
                        {getIcon(app.iconName)}
                      </div>
                      <Badge variant="secondary" className="font-normal text-xs">
                        {app.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-semibold text-card-foreground">
                      {app.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground mt-2 leading-normal">
                      {app.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-2 pb-6">
                    <div className="border-t border-border/60 pt-4 space-y-2.5">
                      <div className="flex items-start gap-2.5 text-sm text-foreground/90">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{app.pointsText1}</span>
                      </div>
                      <div className="flex items-start gap-2.5 text-sm text-foreground/90">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{app.pointsText2}</span>
                      </div>
                      <div className="flex items-start gap-2.5 text-sm text-foreground/90">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{app.pointsText3}</span>
                      </div>
                    </div>
                  </CardContent>
                </div>

                <CardFooter className="pt-0">
                  <Button asChild variant="outline" className="w-full justify-between group">
                    <Link to={app.linkPath}>
                      <span>{app.ctaText}</span>
                      <ArrowRight className="size-4 text-muted-foreground group-hover:text-foreground group-hover:translate-x-0.5 transition-all" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>
      </div>
    </section>;
}